import { animate, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatRippleModule } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { GAEventKeys, Game } from '@fantaz/shared/data-access/models';
import { UIStore } from '@fantaz/shared/data-access/store';
import { SharedDirectivesSrcErrorModule } from '@fantaz/shared/directives/src-error';
import { SwiperOptions } from 'swiper';

@Component({
  selector: 'fz-game-list-picker',
  standalone: true,
  imports: [
    SharedDirectivesSrcErrorModule,
    MatCardModule,
    MatRippleModule,
    MatTooltipModule,
  ],
  templateUrl: './game-list-picker.component.html',
  styleUrls: ['./game-list-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('fadeSlideInOut', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(20px)' }),
        animate('500ms', style({ opacity: 1, transform: 'translateX(0)' })),
      ]),
      transition(':leave', [
        animate('500ms', style({ opacity: 0, transform: 'translateX(10px)' })),
      ]),
    ]),
  ],
})
export class GameListPickerComponent {
  @Input() games: Game[];

  @Input()
  selected!: string[];

  @Input()
  selectionMultiple = false;

  @Output()
  selectionChanged = new EventEmitter<string[]>();

  constructor(private readonly uiStore: UIStore) {}

  getGameImage(game: Game): string {
    const result = game.key.split('.');
    const [gameName, version] = result || ['default', 'v1'];

    return `/shared/img/games/${gameName}/${version}/${gameName}-avatar.webp`;
  }

  /**
   * Function to handle game click selection
   *
   * @param game Game
   */
  onGameSelected(game: Game) {
    const isSelected = this.isSelected(game);
    this.uiStore.reportToGoogleAnalytics({
      event: GAEventKeys.CUSTOM_CLICK,
      ga_value: 'card-game-picker',
    });

    if (isSelected) {
      this.selectionChanged.emit(
        this.selected.filter((key) => key !== game.key),
      );
    } else {
      this.selectionChanged.emit(
        this.selected ? this.selected.concat([game.key]) : [game.key],
      );
    }
  }

  /**
   * Function to check if game is selected
   *
   * @param game Game
   * @returns boolean
   */
  isSelected(game: Game): boolean {
    if (this.selected && this.selected.length > 0) {
      return this.selected.findIndex((key) => key === game.key) > -1;
    }
    return false;
  }

  isFirstChildSelected(): boolean {
    if (
      this.games &&
      this.games.length > 0 &&
      this.selected &&
      this.selected.length > 0
    ) {
      return this.isSelected(this.games[0]);
    }

    return false;
  }

  isLastChildSelected(): boolean {
    if (
      this.games &&
      this.games.length > 0 &&
      this.selected &&
      this.selected.length > 0
    ) {
      return this.isSelected(this.games[this.games.length - 1]);
    }

    return false;
  }
}
